import { useNavigate, useLocation } from "react-router-dom";
import { Gamepad2, Gift, Menu } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * BottomNav — player tab bar. Colors come from the theme tokens (which
 * DynamicBranding derives from the tenant's brand), so the bar always matches
 * the page it sits on, including the corporate light theme on /c/default.
 */
const BottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const extractClientSlug = (pathname: string): string => {
    const match = pathname.match(/^\/c\/([^\/]+)/);
    return match ? match[1] : "default";
  };

  const currentSlug = extractClientSlug(location.pathname);
  const baseRoute = `/c/${currentSlug}`;

  const isActive = (path: string) => {
    const fullPath = path === "/" ? baseRoute : `${baseRoute}${path}`;
    if (path === "/") {
      return location.pathname === baseRoute || location.pathname === `${baseRoute}/`;
    }
    return location.pathname.startsWith(fullPath);
  };

  const navItems = [
    { path: "/", label: "Jouer", icon: Gamepad2 },
    { path: "/vouchers", label: "Coupons", icon: Gift },
    { path: "/menu", label: "Profile", icon: Menu },
  ];

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-border/70 bg-background/90 backdrop-blur-md safe-area-bottom"
      aria-label="Navigation joueur"
    >
      <div className="mx-auto max-w-2xl px-3 pt-2 pb-2">
        <div className="grid grid-cols-3 gap-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            const fullPath = item.path === "/" ? baseRoute : `${baseRoute}${item.path}`;

            return (
              <button
                key={item.path}
                type="button"
                onClick={() => navigate(fullPath)}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "pressable flex h-14 flex-col items-center justify-center gap-1 rounded-2xl text-xs transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background",
                  active
                    ? "bg-primary/[0.12] font-bold text-primary-ink"
                    : "font-medium text-muted-foreground hover:text-foreground",
                )}
              >
                <Icon className="h-5 w-5" strokeWidth={active ? 2.4 : 2} aria-hidden="true" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default BottomNav;
